import { styled } from '@linaria/react';
import { Flex, Stack } from '@mantine/core';
import * as Sentry from '@sentry/react';
import type { TooltipProps } from 'recharts';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { HEIGHT_BREAKPOINTS } from '../../../../styles/breakpoints';
import {
  baseWhite,
  borderRadiusXtiny,
  boxShadowSmallSoft,
  fontSizeSmall,
  fontWeightMedium,
  fontWeightSemibold,
  inkDark,
  inkLight,
  skyLight,
} from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { shouldDisplayValue } from './line-chart.helpers';
import type { LineChartTooltipProps, PayloadEntry, TooltipPayload } from './line-chart.type';

type Props = TooltipProps<number, string> & LineChartTooltipProps;

export const LineChartTooltip = ({
  formatItemName = (name) => name,
  formatValue,
  targetIds = [],
  ...tooltipProps
}: Props) => {
  const { height } = useWindowSize();
  const { active, payload, label } = tooltipProps as TooltipPayload;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  try {
    const validPayload = payload.filter(
      (entry) =>
        shouldDisplayValue(entry.value) &&
        typeof entry.dataKey === 'string' &&
        !entry.dataKey.includes('_trend') &&
        !targetIds.includes(entry.dataKey),
    );

    if (validPayload.length === 0) {
      return null;
    }

    const isExpandedTooltip = height > parseInt(HEIGHT_BREAKPOINTS.md, 10);
    const dateLabel = payload[0]?.payload?.dateRange || payload[0]?.payload?.date || label;

    return (
      <TooltipContainer gap={isExpandedTooltip ? 'xs' : 0} p={isExpandedTooltip ? 'sm' : '8px 12px'}>
        <DateLabelContainer>
          <DateLabel size="small" weight="medium">
            {dateLabel}
          </DateLabel>
        </DateLabelContainer>

        <Stack gap={isExpandedTooltip ? 'xs' : 2}>
          {validPayload.map((entry: PayloadEntry, index: number) => {
            const itemName = formatItemName(entry.name);
            const { value } = entry;

            return (
              <Row key={`item-${index}`} align="center" justify="space-between" gap={16}>
                <LeftContent align="center" gap={8}>
                  <ColorIndicator $color={entry.color} />
                  <Name size="small">{itemName}</Name>
                </LeftContent>
                <RightContent>
                  <Value size="small" weight="medium">
                    {formatValue(value, entry.name)}
                  </Value>
                </RightContent>
              </Row>
            );
          })}
        </Stack>
      </TooltipContainer>
    );
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

const TooltipContainer = styled(Stack)`
  background: ${baseWhite};
  border-radius: ${borderRadiusXtiny};
  box-shadow: ${boxShadowSmallSoft};
  border: 1px solid ${skyLight};
  max-width: 320px;
  min-width: fit-content;
  width: auto;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`;

const DateLabelContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 4px;
`;

const DateLabel = styled(Text)`
  color: ${inkDark};
  font-weight: ${fontWeightSemibold};
  font-size: ${fontSizeSmall};
  line-height: 1.2;
`;

const Row = styled(Flex)`
  transition: background-color 0.15s ease;
  border-radius: 4px;
  padding: 2px 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const LeftContent = styled(Flex)`
  flex: 0 1 auto;
  min-width: 0;
`;

const RightContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  min-width: 100px;
  white-space: nowrap;
`;

const ColorIndicator = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  border: 2px solid ${baseWhite};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const Name = styled(Text)`
  color: ${inkLight};
  font-size: ${fontSizeSmall};
  font-weight: ${fontWeightMedium};
  line-height: 1.3;
`;

const Value = styled(Text)`
  color: ${inkDark};
  font-size: ${fontSizeSmall};
  font-weight: ${fontWeightSemibold};
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
`;
